// 承認状態バナーの見出しと色の写像 (AC-BSP-067、 #7504)。 通常入札・ブロック入札で共有する。
// 承認軸の SUBMITTED は JEPX への送信成功を意味しないので、 承認済 (承認軸 APPROVED / SUBMITTED) は
// 送信軸と入札締切の前後で見出しを分ける (#7505)。
import {
  BiddingApprovalMode,
  BiddingApprovalState,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import { type BidMatchOutcome, deriveJepxReach } from './bidStatusCategory'
import { type SpotMarketPhase, isSpotBeforeDeadline } from './marketPhase'

export type ApprovalBannerHeading =
  | 'pending' // 承認待ち
  | 'approvedSending' // 承認済 (JEPX への送信が完了する前・入札締切前。 送信待ち・送信中・自動再送中を含む)
  | 'approvedUnconfirmed' // 承認済 (入札締切に到達しても JEPX への送信を確認できない)
  | 'approvedReserved' // 承認済 (予約入札フェーズ。 受付開始まで JEPX へ送信しない)
  | 'active' // 入札中 (JEPX が受け付けた)
  | 'rejected' // 差戻し

/** 承認済 (承認軸 APPROVED / SUBMITTED) の依頼が取りうる見出し (承認待ち・差戻しを除く)。 */
export type ApprovedBannerHeading = Exclude<
  ApprovalBannerHeading,
  'pending' | 'rejected'
>

export const isApprovedBannerHeading = (
  heading: ApprovalBannerHeading,
): heading is ApprovedBannerHeading =>
  heading !== 'pending' && heading !== 'rejected'

/**
 * 見出しごとのバナーの色 (AC-BSP-067 ※: 承認待ち = warning、 承認済 (送信完了前・予約入札)・
 * 入札中 = positive、 入札締切に到達しても送信を確認できない承認済 = warning、
 * 差戻し = negative)。 DS `Alert` の status に写す (positive = success、
 * negative = error)。
 */
export const APPROVAL_BANNER_ALERT_STATUS: Record<
  ApprovalBannerHeading,
  'success' | 'warning' | 'error'
> = {
  pending: 'warning',
  approvedSending: 'success',
  approvedUnconfirmed: 'warning',
  approvedReserved: 'success',
  active: 'success',
  rejected: 'error',
}

/**
 * 見出しごとの文言 key。 AC-BSP-067 は見出しを通常入札・ブロック入札で同一文言と定めるので、
 * 両バナーがこの 1 組を引く (ブロック側に別の組を持たない)。 予約入札フェーズの見出しは
 * `openAt` (受付開始日時) を差し込む。
 */
export const APPROVAL_BANNER_TITLE_KEY: Record<ApprovalBannerHeading, string> =
  {
    pending: 'page:biddingSpot.approvalBanner.pendingTitle',
    approvedSending: 'page:biddingSpot.approvalBanner.approvedTitle',
    approvedUnconfirmed:
      'page:biddingSpot.approvalBanner.approvedUnconfirmedTitle',
    approvedReserved: 'page:biddingSpot.approvalBanner.approvedReservedTitle',
    active: 'page:biddingSpot.approvalBanner.submittedTitle',
    rejected: 'page:biddingSpot.approvalBanner.rejectedTitle',
  }

/**
 * 依頼の札 (ブロックはレグ) の約定軸から求めた、 JEPX の判定が確定しているか。 札ごとの規則
 * (`deriveBidStatusCategory` の優先順位 1) と違い、 依頼の全件がそろって同じ確定になった時だけ
 * 確定とみなす。
 */
type BannerBidOutcome =
  | 'accepted' // JEPX が評価した (約定または不約定)
  | 'failed' // 全件が失効した
  | 'pending'

const resolveBannerBidOutcome = (
  outcomes: readonly BidMatchOutcome[],
): BannerBidOutcome => {
  if (outcomes.length === 0) {
    return 'pending'
  }
  if (outcomes.every((o) => o === 'matched' || o === 'unmatched')) {
    return 'accepted'
  }
  if (outcomes.every((o) => o === 'failed')) {
    return 'failed'
  }
  return 'pending'
}

export type ApprovalBannerHeadingInput = {
  approvalMode: BiddingApprovalMode
  approvalState: BiddingApprovalState
  /** 承認依頼 (通常入札) / グループ (ブロック入札) の送信軸。 */
  sendStatus: BiddingBidSendStatus
  /** 画面の市場フェーズ。 入札締切の前後と予約入札フェーズの判定に使う。 */
  phase: SpotMarketPhase
  /** 依頼の札 (ブロックはレグ) の約定軸。 JEPX の判定が確定しているかを求める。 */
  matchOutcomes: readonly BidMatchOutcome[]
}

/**
 * 承認依頼 1 件のバナー見出しを返す。 バナーを出さない依頼は undefined。
 *
 * 承認モードが要承認の依頼だけ出す (AC-BSP-067)。 承認なし・自動は承認を経ていないので出さず、
 * 未設定は要承認と確定できないので出さない。 入札締切の後は、 承認も再申請もできず「承認者の確認待ち」
 * 「修正して再申請」 が事実と合わなくなるので、 承認待ち・差戻し・JEPX が受け付けた依頼 (入札中) は
 * 出さず、 送信を確認できない承認済の依頼だけ出す。
 * 約定軸の確定値 (JEPX の評価) は送信軸より優先する (ステータス列と同じ優先順位。 ただし
 * 確定とみなすのは依頼の全件がそろった時だけ)。
 * 「送信失敗」 の見出しは無い (AC-BSP-067)。 入札締切前に送信失敗が確定した札
 * (SEND_FAILED_FINAL) と、 入札締切前に全件が失効した依頼は、 受け付けられず承認ステータスが
 * 無くなった札と同じくバナーを出さない。 自動再送中 (SEND_FAILED) は入札締切前に成功しうるので
 * 承認済のまま。
 * 入札締切後に JEPX へ届いた記録が無い札 ([[deriveJepxReach]] が reached でない札) は、 失敗を
 * 確認したのではなく送信を確認できないので「送信を確認できない」 とする。 ステータス列は同じ札の
 * うち届いていない札を失効の代理判定にするが、 バナーは全件が失効しても確認できないままにする
 * (JEPX が受け付けていたかを確かめている札 AC-BSP-117 と見分けられないため)。
 */
export const resolveApprovalBannerHeading = (
  input: ApprovalBannerHeadingInput,
): ApprovalBannerHeading | undefined => {
  if (input.approvalMode !== BiddingApprovalMode.APPROVAL) {
    return undefined
  }
  switch (input.approvalState) {
    case BiddingApprovalState.PENDING:
      return isSpotBeforeDeadline(input.phase) ? 'pending' : undefined
    case BiddingApprovalState.REJECTED:
      return isSpotBeforeDeadline(input.phase) ? 'rejected' : undefined
    case BiddingApprovalState.APPROVED:
    case BiddingApprovalState.SUBMITTED:
      return resolveApprovedHeading(input)
    default:
      return undefined
  }
}

const resolveApprovedHeading = ({
  sendStatus,
  phase,
  matchOutcomes,
}: ApprovalBannerHeadingInput): ApprovedBannerHeading | undefined => {
  if (sendStatus === BiddingBidSendStatus.CANCELED) {
    return undefined
  }
  const bidOutcome = resolveBannerBidOutcome(matchOutcomes)
  if (bidOutcome === 'accepted') {
    return activeHeadingFor(phase)
  }
  const isBeforeDeadline = isSpotBeforeDeadline(phase)
  if (bidOutcome === 'failed' && isBeforeDeadline) {
    return undefined
  }
  // 入札締切後に失効した札 (JEPX に届いていなかった札) を含む依頼は、 送信軸が受付済みでも
  // 「送信を確認できない」 で出し続ける (AC-BSP-067。 全件の確定を待たずに出す)。
  if (
    !isBeforeDeadline &&
    matchOutcomes.some((outcome) => outcome === 'failed')
  ) {
    return 'approvedUnconfirmed'
  }
  if (deriveJepxReach(sendStatus) === 'reached') {
    return activeHeadingFor(phase)
  }
  if (!isBeforeDeadline) {
    return 'approvedUnconfirmed'
  }
  if (sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL) {
    return undefined
  }
  return phase === 'reserved' ? 'approvedReserved' : 'approvedSending'
}

/**
 * JEPX が受け付けた依頼の見出し。 入札中 (送信済み) の案内は入札締切までで役目を終えるので、
 * 入札締切の後はバナーを出さない (AC-BSP-067、 #10562)。 締切後も知らせるのは、 送信を確認できない
 * 承認済の依頼だけ (承認待ち・差戻しも締切後は出さない)。
 */
const activeHeadingFor = (phase: SpotMarketPhase): 'active' | undefined =>
  isSpotBeforeDeadline(phase) ? 'active' : undefined
